import { useState, useEffect, useRef } from "react";
import { fmt$ } from "./reportUtils";
import Logo from "@/components/Logo";
import { PLANS, FOUNDING_CODE } from "@/config/pricing";

const UNLOCK_URL = "https://buy.stripe.com/eVq5kE82IaJMbARb7W9fW00";

/* ── Countdown Timer Hook ── */
function useCountdown() {
  const [remaining, setRemaining] = useState(() => {
    const stored = sessionStorage.getItem("rankify-countdown-start");
    if (stored) {
      const elapsed = Math.floor((Date.now() - parseInt(stored)) / 1000);
      return Math.max(0, 47 * 3600 + 59 * 60 - elapsed);
    }
    sessionStorage.setItem("rankify-countdown-start", String(Date.now()));
    return 47 * 3600 + 59 * 60;
  });

  useEffect(() => {
    const interval = setInterval(() => setRemaining(prev => Math.max(0, prev - 1)), 1000);
    return () => clearInterval(interval);
  }, []);

  const h = Math.floor(remaining / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = remaining % 60;
  const isUrgent = remaining < 3600;
  const formatted = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return { formatted, isUrgent };
}

/* ── Social Proof Hook ── */
function useSocialProof() {
  const [lastUnlock, setLastUnlock] = useState("4 minutes ago");
  const intervalRef = useRef(null);
  useEffect(() => {
    const times = ["2 minutes ago", "4 minutes ago", "7 minutes ago", "3 minutes ago", "5 minutes ago", "1 minute ago", "6 minutes ago"];
    const schedule = () => {
      intervalRef.current = setTimeout(() => {
        setLastUnlock(times[Math.floor(Math.random() * times.length)]);
        schedule();
      }, 180000 + Math.floor(Math.random() * 300000));
    };
    schedule();
    return () => clearTimeout(intervalRef.current);
  }, []);
  return lastUnlock;
}

/* ═══════════════════════════════════════════
   C8 Bottom CTA — Full Dark Section
   ═══════════════════════════════════════════ */
export default function BottomCTASection({
  revenue_impact = {},
  competitor_comparison = {},
  ai_visibility = null,
  citation_status = {},
  scores = {},
  issues = [],
  hasAccess,
  handleUnlock,
  checkoutLoading,
  issueCards,
}) {
  const { formatted, isUrgent } = useCountdown();
  const lastUnlock = useSocialProof();
  const loss = revenue_impact?.monthly_revenue_loss || 0;

  // Pain card data from real audit
  const competitors = competitor_comparison?.competitors || [];
  // Sort by score descending, find our rank position
  const sorted = [...competitors].sort((a, b) => (b.total || 0) - (a.total || 0));
  const yourRank = sorted.length > 0
    ? sorted.findIndex(c => c.is_you) + 1 || sorted.length
    : 4;
  const totalCompetitors = sorted.length || 4;

  // Citation score: count accurate vs total directories
  const citDirs = citation_status?.directories || [];
  const citAccurate = citDirs.filter(d => d.status === "accurate").length;
  const citTotal = Math.max(citDirs.length, 1);
  const citScore = citTotal > 0 ? Math.round((citAccurate / citTotal) * 100) : 36;

  // AI visibility: use platforms array from the dict format
  const aiPlatforms = ai_visibility?.platforms || (Array.isArray(ai_visibility) ? ai_visibility : []);
  const aiMentioned = aiPlatforms.filter(r => r.status === "found" || r.mentioned || r.found).length;
  const aiTotal = Math.max(aiPlatforms.length, 2);

  const issueCount = issueCards ? issueCards.length : (issues?.length || 8);

  if (hasAccess) {
    return (
      <div className="report-section-pad" style={{
        background: "#0f172a", borderRadius: 16, padding: "48px 36px",
        textAlign: "center", marginTop: 8,
      }} data-testid="c8-unlocked-footer">
        <Logo size="default" theme="dark" />
        <h2 style={{ fontSize: 26, fontWeight: 900, color: "#fff", marginTop: 20, marginBottom: 8 }}>
          Your Full Report Is Unlocked
        </h2>
        <p style={{ fontSize: 15, color: "#9CA3AF", margin: "0 auto 24px", maxWidth: 480, lineHeight: 1.7 }}>
          Follow your 90-day plan step by step. Your score projection: {scores?.overall || 0} &rarr; {Math.min((scores?.overall || 0) + 38, 100)}.
        </p>
        <div style={{ textAlign: "center", padding: "24px 0 0", fontSize: 12, color: "#475569" }}>
          &copy; {new Date().getFullYear()} RankifyLocal. All rights reserved.
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#0f172a", borderRadius: 16, marginTop: 8, overflow: "hidden" }} data-testid="c8-bottom-cta">

      {/* ═════ SECTION 1: Pain Summary ═════ */}
      <div className="report-section-pad" style={{ padding: "40px 20px 32px", textAlign: "center" }}>
        <h2 style={{ fontSize: "clamp(20px, 4.5vw, 30px)", fontWeight: 900, color: "#fff", margin: "0 0 10px", wordBreak: "break-word", overflowWrap: "break-word" }} data-testid="c8-headline">
          Stop Losing {fmt$(loss)} Every Month
        </h2>
        <p style={{ fontSize: 14, color: "#94A3B8", margin: "0 auto 28px", maxWidth: 420, lineHeight: 1.7 }}>
          Your competitors are winning customers you should be getting right now.
        </p>

        {/* 3 Pain Cards */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: 12, maxWidth: 640, margin: "0 auto",
        }} data-testid="c8-pain-cards">
          {[
            { label: "Rankings", metric: `#${yourRank} of ${totalCompetitors}`, status: yourRank > totalCompetitors / 2 ? "POOR" : "FAIR" },
            { label: "Citations", metric: `${citScore}/100`, status: citScore < 50 ? "POOR" : citScore < 70 ? "FAIR" : "OK" },
            { label: "AI Search", metric: `${aiMentioned} of ${aiTotal}`, status: aiMentioned === 0 ? "INVISIBLE" : aiMentioned < aiTotal ? "WEAK" : "OK" },
          ].map((card, i) => (
            <div key={i} style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderTop: "3px solid #ef4444",
              borderRadius: 12, padding: "18px 14px", textAlign: "center",
            }} data-testid={`c8-pain-card-${i}`}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#94A3B8", marginBottom: 8 }}>
                {card.label}
              </p>
              <p style={{ fontSize: "clamp(20px, 5vw, 28px)", fontWeight: 900, color: "#ef4444", margin: "0 0 6px", lineHeight: 1 }}>
                {card.metric}
              </p>
              <p style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: card.status === "INVISIBLE" ? "#f97316" : "#ef4444" }}>
                {card.status}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ═════ SECTION 2: What You Unlock ═════ */}
      <div className="report-section-pad" style={{ padding: "0 36px 40px" }}>
        <p style={{ fontSize: 20, fontWeight: 700, color: "#fff", textAlign: "center", marginBottom: 20 }}>
          What you get for $27:
        </p>
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "8px 32px", maxWidth: 640, margin: "0 auto",
        }} data-testid="c8-unlock-benefits">
          {[
            `Complete fix plan (${issueCount} issues)`,
            "Copy-paste GBP description",
            "Citation NAP fix sheet",
            "LocalBusiness schema code",
            "Full 5x5 geo-grid map (25 points)",
            "90-day action roadmap",
            "Competitor gap analysis",
            "AI visibility improvement plan",
          ].map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span style={{ fontSize: 15, color: "rgba(255,255,255,0.9)" }}>{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ═════ SECTION 3: Main CTA Button ═════ */}
      <div className="report-section-pad" style={{ padding: "0 36px 40px", textAlign: "center" }}>
        <button
          onClick={() => window.open(UNLOCK_URL, "_blank")}
          disabled={checkoutLoading}
          data-testid="c8-unlock-btn"
          style={{
            display: "inline-block", width: "100%", maxWidth: 480,
            background: "#6366f1", color: "#fff",
            fontWeight: 800, fontSize: 18,
            padding: "16px 32px", borderRadius: 12,
            border: "none", cursor: "pointer",
            transition: "background 0.2s",
          }}
          onMouseOver={e => { e.currentTarget.style.background = "#4f46e5"; }}
          onMouseOut={e => { e.currentTarget.style.background = "#6366f1"; }}
        >
          {checkoutLoading ? "Processing..." : "Unlock Full Report \u2014 $27 \u2192"}
        </button>

        {/* Social proof below button */}
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 16, marginTop: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <svg width="14" height="14" fill="none" stroke={isUrgent ? "#FCA5A5" : "#FBBF24"} strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
            </svg>
            <span style={{ fontSize: 13, fontWeight: 700, color: isUrgent ? "#FCA5A5" : "#FDE68A", fontFamily: "monospace" }}>
              {formatted}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <svg width="14" height="14" fill="none" stroke="#94A3B8" strokeWidth="2" viewBox="0 0 24 24">
              <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#94A3B8" }}>2,847 businesses audited this month</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ color: "#FBBF24", fontSize: 14 }}>&#9733;</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#94A3B8" }}>Last unlock: {lastUnlock}</span>
          </div>
        </div>
      </div>

      {/* ═════ SECTION 4: Monthly Plans ═════ */}
      <div className="report-section-pad" style={{ padding: "0 36px 48px" }}>
        <div style={{ height: 1, background: "rgba(255,255,255,0.1)", margin: "0 0 40px" }} />
        <p style={{ fontSize: 18, fontWeight: 700, color: "#fff", textAlign: "center", marginBottom: 24 }}>
          Or get everything done-for-you:
        </p>

        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 16, maxWidth: 780, margin: "0 auto",
        }} data-testid="c8-monthly-plans">
          {PLANS.map((plan, i) => (
            <div key={plan.id} style={{
              background: "rgba(255,255,255,0.05)",
              border: plan.popular ? "2px solid #10B981" : "1px solid rgba(255,255,255,0.1)",
              borderRadius: 12, padding: "24px 20px", position: "relative",
            }} data-testid={`c8-plan-card-${i}`}>
              {plan.popular && (
                <div style={{
                  position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)",
                  background: "#10B981", color: "#fff", fontSize: 10, fontWeight: 800,
                  padding: "3px 12px", borderRadius: 4, letterSpacing: "0.04em",
                }}>MOST POPULAR</div>
              )}
              <p style={{ fontSize: 14, fontWeight: 700, color: plan.color, marginBottom: 4 }}>{plan.name}</p>
              {/* Strikethrough regular → founding price */}
              <p style={{ fontSize: 13, color: "#64748B", textDecoration: "line-through", margin: "0 0 2px" }}>
                ${plan.regularPrice}<span style={{ fontSize: 12 }}>{plan.period}</span>
              </p>
              <p style={{ fontSize: 30, fontWeight: 900, color: "#fff", margin: "0 0 2px" }}>
                ${plan.foundingPrice}<span style={{ fontSize: 13, fontWeight: 500, color: "#94A3B8" }}>{plan.period}</span>
              </p>
              <p style={{ fontSize: 10, fontWeight: 700, color: "#FBBF24", letterSpacing: ".04em", marginBottom: 6 }}>
                code {FOUNDING_CODE} — 35% off, locked forever
              </p>
              <p style={{ fontSize: 12, color: "#94A3B8", lineHeight: 1.5, marginBottom: 10 }}>{plan.tagline}</p>
              {plan.trialNote && <p style={{ fontSize: 11, fontWeight: 700, color: "#3ECF8E", marginBottom: 8 }}>{plan.trialNote}</p>}
              <div style={{ height: 1, background: "rgba(255,255,255,0.1)", margin: "8px 0 10px" }} />
              {plan.features.map((f, j) => (
                <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 5 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={plan.color} strokeWidth="3" style={{ flexShrink: 0, marginTop: 2 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <span style={{ fontSize: 12, color: "#D1D5DB", lineHeight: 1.4 }}>{f}</span>
                </div>
              ))}
              {/* FOUNDING35 promo code is appended to all Stripe links */}
              <button
                onClick={() => window.open(plan.stripeLink, "_blank")}
                data-testid={`c8-plan-btn-${i}`}
                style={{
                  width: "100%", marginTop: 16,
                  background: plan.popular ? plan.color : "transparent",
                  border: plan.popular ? "none" : `1px solid ${plan.color}`,
                  color: plan.popular ? "#fff" : plan.color,
                  fontWeight: 700, fontSize: 13, padding: "10px", borderRadius: 8, cursor: "pointer",
                }}
              >
                Get Started
              </button>
            </div>
          ))}
        </div>

        <p style={{ textAlign: "center", fontSize: 12, color: "#64748B", marginTop: 16, lineHeight: 1.6 }}>
          Your $27 report fee is credited toward your first month on any monthly plan.
        </p>
      </div>

      {/* Footer */}
      <div style={{ textAlign: "center", padding: "0 0 24px", fontSize: 12, color: "#475569" }}>
        &copy; {new Date().getFullYear()} RankifyLocal. All rights reserved.
      </div>
    </div>
  );
}
